import type { FC, RefObject } from 'react';
import { useContext, useRef } from 'react';
import type { BoxProps } from '@mui/material';
import { Box, Collapse } from '@mui/material';
import { isArray } from 'lodash';

import { AccordionItemContext } from 'src/components/common/Accordion/components/AccordionItem';

import { styles } from './index.styles';

export interface AccordionItemPanelProps extends BoxProps {
  fullHeight?: boolean;
  disableAnimation?: boolean;
}

const AccordionItemPanel: FC<AccordionItemPanelProps> = ({
  children,
  fullHeight = false,
  disableAnimation = false,
  ...props
}) => {
  const elementRef: RefObject<HTMLElement> = useRef(null);

  const { isActive } = useContext(AccordionItemContext);

  return (
    <Collapse
      in={isActive}
      {...(!disableAnimation
        ? {
            timeout: 500,
            onEnter: (node) => {
              node.style.height = '0px'; // Start from collapsed state
            },
            onEntering: (node) => {
              const actualHeight = elementRef.current?.scrollHeight;
              node.style.transition = 'height 0.5s ease-in-out'; // Slow initial expansion
              node.style.height = `calc(${actualHeight}px + 30px)`; // Overshoot
            },
            onEntered: (node) => {
              node.style.transition = 'height 0.2s ease-in-out'; // Faster settle
              node.style.height = `auto`; // Settle at actual height
            },
            onExit: (node) => {
              node.style.transition = 'height 0.3s ease-in';
              node.style.height = '0px'; // Collapse smoothly
            },
          }
        : {})}
      sx={fullHeight ? styles.collapseWrapper : undefined}
    >
      <Box
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
          }
        }}
        {...props}
        role="presentation"
        className="AccordionItemPanel-root"
        ref={elementRef}
        sx={[
          styles.accordionItemPanel,
          ...(isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      >
        {children}
      </Box>
    </Collapse>
  );
};

export default AccordionItemPanel;
